import type { AgencyVisibility } from '@saaga/shared/interfaces/agency.interface'
import { agentsApiSlice } from '@saaga/shared/store/agents-api.store'
import { useUpdateAgencyMutation } from '@saaga/shared/store/api.store'
import { useAppDispatch } from '@saaga/shared/store/store'
import { logger } from '@saaga/shared/utils/logger.util'
import { useCallback } from 'react'
import { toast } from 'sonner'

import { buildVisibilityPayload } from './agency-form-mapper.util'

interface UseAgencyVisibilityActionsProps {
  slug: string
}

export interface UseAgencyVisibilityActionsReturn {
  isLoading: boolean
  handleSubmitForReview: () => Promise<void>
  handleCancelReview: () => Promise<void>
}

export const useAgencyVisibilityActions = ({
  slug,
}: UseAgencyVisibilityActionsProps): UseAgencyVisibilityActionsReturn => {
  const dispatch = useAppDispatch()
  const [updateAgency, { isLoading }] = useUpdateAgencyMutation()

  const transitionVisibility = useCallback(
    async (targetVisibility: AgencyVisibility, toastMessage: string): Promise<void> => {
      try {
        const payload = buildVisibilityPayload(targetVisibility)
        await updateAgency({ slug, payload }).unwrap()
        dispatch(
          agentsApiSlice.util.invalidateTags([
            { type: 'Agencies', id: 'LIST' },
            { type: 'Agencies', id: slug },
          ])
        )
        toast.success(toastMessage)
      } catch (error) {
        logger.error(`Failed to change agency visibility to ${targetVisibility}:`, error)
        toast.error('Failed to update agency visibility')
      }
    },
    [slug, updateAgency, dispatch]
  )

  const handleSubmitForReview = useCallback(
    () => transitionVisibility('pending_review', 'Agency submitted for review'),
    [transitionVisibility]
  )

  const handleCancelReview = useCallback(
    () => transitionVisibility('private', 'Review cancelled'),
    [transitionVisibility]
  )

  return {
    isLoading,
    handleSubmitForReview,
    handleCancelReview,
  }
}
